// Intervalles d'intro et de générique de fin, façon ani-skip.
//
// Deux services publics suffisent, et tous deux répondent avec les en-têtes
// CORS qu'il faut : ils sont donc appelés directement par la page, sans passer
// par le proxy — contrairement aux sources d'animés, ce sont des API faites
// pour les navigateurs.
//
//   AniList  : titre → identifiant MyAnimeList
//   AniSkip  : (identifiant MAL, numéro d'épisode) → intervalles
//
// Le maillon fragile est l'appariement : nos sources ne connaissent qu'un titre
// et une numérotation maison. Tout est donc écrit pour qu'une correspondance
// fausse ou absente ne se voie pas — au pire, aucun bouton n'apparaît.

const ANILIST = "https://graphql.anilist.co";
const ANISKIP = "https://api.aniskip.com/v2";

const CACHE_KEY = "aniweb-mal";

/** Écart maximal toléré entre la durée de notre flux et celle qu'AniSkip a
 *  servi de référence. Au-delà, on tient l'épisode pour mal apparié. */
const LENGTH_TOLERANCE = 90;

export interface SkipInterval {
  id: string;
  type: "op" | "ed";
  start: number;
  end: number;
}

export interface SkipQuery {
  /** Clé stable de la saison, pour ne chercher l'identifiant qu'une fois. */
  cacheKey: string;
  animeName: string;
  seasonName: string | null;
  episode: number;
  duration: number;
}

export function label(type: "op" | "ed"): string {
  return type === "op" ? "Passer l'intro" : "Passer la fin";
}

/** Retire ce que les sources ajoutent au titre et qu'AniList ne connaît pas. */
function cleanTitle(name: string): string {
  return name
    .replace(/\((?:VF|VOSTFR|VOSTFR\/VF)\)/gi, "")
    .replace(/\b(?:VF|VOSTFR)\b/gi, "")
    .replace(/\s+/g, " ")
    .trim();
}

/** anime-sama nomme ses panneaux « Saison 2 » ; AniList référence les suites
 *  comme des entrées distinctes, retrouvées en accolant le numéro au titre
 *  (« One Punch Man 2 » → One Punch Man 2). Les panneaux qui ne parlent pas de
 *  saison — « Film », « Saga 1 », « OAV » — retombent sur la série de base. */
export function seasonNumber(seasonName: string | null): number {
  if (!seasonName) return 1;

  const match = /saison\s*(\d+)/i.exec(seasonName);

  return match ? Number(match[1]) : 1;
}

export function searchTerm(animeName: string, seasonName: string | null): string {
  const title = cleanTitle(animeName);
  const season = seasonNumber(seasonName);

  return season > 1 ? `${title} ${season}` : title;
}

function readCache(): Record<string, number> {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return {};

    const parsed: unknown = JSON.parse(raw);

    return typeof parsed === "object" && parsed !== null
      ? (parsed as Record<string, number>)
      : {};
  } catch {
    return {};
  }
}

function writeCache(cache: Record<string, number>): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
  } catch {
    // stockage indisponible : on refera la recherche
  }
}

const ANILIST_QUERY = `query ($search: String) {
  Page(perPage: 5) {
    media(search: $search, type: ANIME, format_not: MUSIC) {
      idMal
    }
  }
}`;

interface AniListResponse {
  data?: { Page?: { media?: { idMal: number | null }[] } };
}

async function searchMalId(term: string): Promise<number | null> {
  const res = await fetch(ANILIST, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ query: ANILIST_QUERY, variables: { search: term } }),
  });

  if (!res.ok) return null;

  const body = (await res.json()) as AniListResponse;
  const media = body.data?.Page?.media ?? [];

  // certaines fiches AniList n'ont pas d'équivalent MyAnimeList
  return media.find((entry) => entry.idMal !== null)?.idMal ?? null;
}

/** L'identifiant ne dépend que de la saison : on ne le cherche qu'une fois, et
 *  seuls les succès sont retenus — un service momentanément indisponible ne
 *  doit pas condamner la série. */
async function malIdFor(query: SkipQuery): Promise<number | null> {
  const cache = readCache();
  const cached = cache[query.cacheKey];

  if (typeof cached === "number") return cached;

  const found = await searchMalId(searchTerm(query.animeName, query.seasonName));

  if (found === null) return null;

  cache[query.cacheKey] = found;
  writeCache(cache);

  return found;
}

interface AniSkipResult {
  interval: { startTime: number; endTime: number };
  skipType: string;
  skipId: string;
  episodeLength: number;
}

interface AniSkipResponse {
  found: boolean;
  results?: AniSkipResult[];
}

/** Un intervalle n'est retenu que s'il tient dans notre épisode et que la durée
 *  de référence concorde : c'est ce qui rattrape la plupart des mauvais
 *  appariements, un film face à une série par exemple. */
function usable(result: AniSkipResult, duration: number): boolean {
  if (duration <= 0) return false;

  if (
    result.episodeLength > 0 &&
    Math.abs(result.episodeLength - duration) > LENGTH_TOLERANCE
  ) {
    return false;
  }

  const { startTime, endTime } = result.interval;

  return endTime > startTime && startTime >= 0 && endTime <= duration + 1;
}

/** Rend les intervalles connus pour cet épisode, ou une liste vide : AniSkip
 *  est alimenté par ses utilisateurs, beaucoup d'épisodes n'y sont pas. */
export async function lookup(query: SkipQuery): Promise<SkipInterval[]> {
  try {
    const malId = await malIdFor(query);
    if (malId === null) return [];

    const params = new URLSearchParams({ episodeLength: String(query.duration) });
    params.append("types", "op");
    params.append("types", "ed");

    const res = await fetch(
      `${ANISKIP}/skip-times/${malId}/${query.episode}?${params}`,
    );

    if (!res.ok) return [];

    const body = (await res.json()) as AniSkipResponse;
    if (!body.found) return [];

    return (body.results ?? [])
      .filter((result) => usable(result, query.duration))
      .map((result) => ({
        id: result.skipId,
        type: result.skipType === "ed" ? ("ed" as const) : ("op" as const),
        start: result.interval.startTime,
        end: result.interval.endTime,
      }));
  } catch {
    // réseau, quota, format inattendu : le bouton ne s'affichera pas
    return [];
  }
}
